import {
  validateDataNascimento,
  validateCPF,
  validateEmail,
  validateCEP,
  validateNumeroCartao,
  validateValidadeCartao,
  validateCVV,
  validateCamposObrigatorios,
  CampoObrigatorio,
} from "./validationUtils";

// Interface para dados básicos
export interface DadosBasicos {
  nome: string;
  telefone: string;
  email: string;
  cpf: string;
  dataNascimento: string;
}

// Interface para dados complementares
export interface DadosComplementares {
  cep: string;
  endereco: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
}

// Interface para dados de pagamento
export interface DadosPagamento {
  nomeCartao: string;
  numeroCartao: string;
  validadeCartao: string;
  cvv: string;
}

// Interface para resultado de validação
export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

// Função para validar dados básicos
export const validateDadosBasicos = (dados: DadosBasicos): ValidationResult => {
  const camposObrigatorios: CampoObrigatorio[] = [
    { campo: dados.nome, nome: "Nome" },
    { campo: dados.telefone, nome: "Telefone" },
    { campo: dados.email, nome: "Email" },
    { campo: dados.cpf, nome: "CPF" },
    { campo: dados.dataNascimento, nome: "Data de Nascimento" },
  ];

  // Validação de campos obrigatórios
  const { isValid, camposVazios } =
    validateCamposObrigatorios(camposObrigatorios);

  if (!isValid) {
    return {
      isValid: false,
      errorMessage: `Preencha os seguintes campos: ${camposVazios.join(", ")}`,
    };
  }

  // Validação específica da data de nascimento
  if (
    dados.dataNascimento.trim() &&
    !validateDataNascimento(dados.dataNascimento)
  ) {
    return {
      isValid: false,
      errorMessage:
        "Data de nascimento inválida. Verifique se a data está no formato DD/MM/AAAA e se é uma data válida.",
    };
  }

  // Validação específica do CPF
  if (dados.cpf.trim() && !validateCPF(dados.cpf)) {
    return {
      isValid: false,
      errorMessage: "CPF inválido. Verifique se o CPF está correto.",
    };
  }

  // Validação específica do Email
  if (dados.email.trim() && !validateEmail(dados.email)) {
    return {
      isValid: false,
      errorMessage:
        "Email inválido. Verifique se o email está no formato correto (exemplo@dominio.com).",
    };
  }

  return { isValid: true };
};

// Função para validar dados complementares
export const validateDadosComplementares = (
  dados: DadosComplementares
): ValidationResult => {
  const camposObrigatorios: CampoObrigatorio[] = [
    { campo: dados.cep, nome: "CEP" },
    { campo: dados.endereco, nome: "Endereço" },
    { campo: dados.numero, nome: "Número" },
    { campo: dados.bairro, nome: "Bairro" },
    { campo: dados.cidade, nome: "Cidade" },
    { campo: dados.estado, nome: "Estado" },
  ];

  // Validação de campos obrigatórios
  const { isValid, camposVazios } =
    validateCamposObrigatorios(camposObrigatorios);
  if (!isValid) {
    return {
      isValid: false,
      errorMessage: `Preencha os seguintes campos: ${camposVazios.join(", ")}`,
    };
  }

  // Validação específica do CEP
  if (dados.cep.trim() && !validateCEP(dados.cep)) {
    return {
      isValid: false,
      errorMessage: "CEP inválido. Verifique se o CEP está correto.",
    };
  }

  return { isValid: true };
};

// Função para validar dados de pagamento
export const validateDadosPagamento = (
  dados: DadosPagamento
): ValidationResult => {
  const camposObrigatorios: CampoObrigatorio[] = [
    { campo: dados.nomeCartao, nome: "Nome do Cartão" },
    { campo: dados.numeroCartao, nome: "Número do Cartão" },
    { campo: dados.validadeCartao, nome: "Validade do Cartão" },
    { campo: dados.cvv, nome: "CVV" },
  ];

  // Validação de campos obrigatórios
  const { isValid, camposVazios } =
    validateCamposObrigatorios(camposObrigatorios);

  if (!isValid) {
    return {
      isValid: false,
      errorMessage: `Preencha os seguintes campos: ${camposVazios.join(", ")}`,
    };
  }

  // Validação específica do número do cartão
  if (dados.numeroCartao.trim() && !validateNumeroCartao(dados.numeroCartao)) {
    return {
      isValid: false,
      errorMessage:
        "Número do cartão inválido. Verifique se o número está correto.",
    };
  }

  // Validação específica da validade do cartão
  if (
    dados.validadeCartao.trim() &&
    !validateValidadeCartao(dados.validadeCartao)
  ) {
    return {
      isValid: false,
      errorMessage:
        "Validade do cartão inválida. Verifique se a data está no formato MM/AA e se não está vencida.",
    };
  }

  // Validação específica do CVV
  if (dados.cvv.trim() && !validateCVV(dados.cvv)) {
    return {
      isValid: false,
      errorMessage:
        "CVV inválido. Verifique se o CVV está correto (3 ou 4 dígitos).",
    };
  }

  return { isValid: true };
};
